/**
 * „Podopieczny progresuje w" — which column the client climbs before the ruled one jumps.
 *
 * ── Every column is LISTED, and the ineligible ones say why ─────────────────────────────────
 * `gateRefusal` has four answers and each of them is a different sentence a trainer needs: this is
 * the column that already jumps, the dictionary says this one does not decide progression, the
 * movement does not measure it, the load is priced as a fraction of a max so there are no kilograms
 * to climb. Hiding them would leave a menu that is two entries long on one exercise and four on the
 * next with nothing saying why — so they are disabled entries carrying their reason, which is also
 * the only shape that puts the reason within reach of the keyboard.
 *
 * A column that already carries a rule of its own is NOT refused: binding the band to it replaces
 * that rule, which is a price rather than a wall, and `gateRefusal`'s own header argues the
 * asymmetry. The menu says so on the entry rather than swallowing the trade.
 */

import {
  availableAxisFields,
  gateRefusal,
  resolveGridColumns,
  ruleDisplacedByBand,
  type ExerciseDraft,
} from '~/entities/plan-draft';
import type { MeasurementFieldKey } from '~/shared/measurement';
import menuStyles from '~/shared/ui/MenuSurface.module.css';

import type { Translate } from '../model/matrixCellState';

import { RefusableRadioMenu } from './RefusableRadioMenu';
import styles from './RuleSentence.module.css';

const KEY = 'web:planBuilder.progression.sheet';

interface GateMenuProps {
  readonly staged: ExerciseDraft;
  /** The column the rule advances — a band may never sit on it. */
  readonly steppedField: MeasurementFieldKey;
  readonly gatedField: MeasurementFieldKey;
  readonly onPick: (field: MeasurementFieldKey) => void;
  readonly t: Translate;
}

export function GateMenu({ staged, steppedField, gatedField, onPick, t }: GateMenuProps) {
  const context = {
    measurementFields: staged.measurementFields,
    progressionRules: staged.progressionRules,
    prescriptionMode: staged.prescriptionMode,
  };
  const columns = resolveGridColumns(availableAxisFields(staged.measurementFields), false);
  const current = columns.find((entry) => entry.measurementField === gatedField);

  return (
    <RefusableRadioMenu
      triggerProps={{
        className: styles.chip,
        /* The bound column rides on the name, because the label replaces the chip's own text — see
          * the family menu next door for the same pairing and the same reason. */
        'aria-label': t(`${KEY}.menuValue`, {
          label: t(`${KEY}.gate.menuLabel`),
          value: current === undefined ? t(`${KEY}.field.gate`) : t(current.field.labelKey),
        }),
        children: (
          <>
            {current === undefined ? t(`${KEY}.field.gate`) : t(current.field.labelKey)}
            <i className={styles.caret} aria-hidden="true">
              ▾
            </i>
          </>
        ),
      }}
      label={t(`${KEY}.gate.menuLabel`)}
      value={gatedField}
      options={columns.flatMap(({ field: column, measurementField }) => {
        if (measurementField === null) return [];
        const refusal = gateRefusal(context, steppedField, measurementField);
        // THE SAME PREDICATE the writer applies and the switch beside this menu warns with.
        const displaced = ruleDisplacedByBand(staged.progressionRules, steppedField, measurementField);
        return [
          {
            value: measurementField,
            isRefused: refusal !== null,
            content: (
              <span className={styles.menuEntry}>
                <span className={menuStyles.itemLabel}>{t(column.labelKey)}</span>
                {refusal !== null && (
                  <span className={styles.menuNote}>{t(`${KEY}.gate.refusal.${refusal}`)}</span>
                )}
                {/* The PRICE of an otherwise legal choice: this column's own rule goes when the band
                  * lands on it, and the trainer is told before the press rather than after. */}
                {refusal === null && displaced !== null && (
                  <span className={styles.menuNote}>{t(`${KEY}.gate.replacesRule`)}</span>
                )}
              </span>
            ),
          },
        ];
      })}
      onPick={onPick}
    />
  );
}
